import { FileText, Paperclip } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { downloadFile } from '@/lib/api/client'
import { displayValue, type GenericRequestDecisionContext } from '@/features/request'

/**
 * The form, paired with the answers.
 *
 * <p>A payload on its own is a bag of keys. What makes a request decidable is the label the
 * administrator gave each field, in the order they arranged them — "Reason for advance:
 * school fees", not {@code {"reason_for_advance":"school fees"}}.
 */
export function GenericRequestContext({ detail }: { detail: GenericRequestDecisionContext }) {
  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold text-[var(--color-heading)]">
            {detail.requestTypeName}
            {detail.requestTypeCategory && (
              <span className="ms-2 text-xs font-normal text-[var(--color-muted)]">
                {detail.requestTypeCategory}
              </span>
            )}
          </h3>
          {detail.slaHours != null && (
            <span className="text-xs text-[var(--color-muted)]">
              promised within {detail.slaHours} hours
            </span>
          )}
        </div>

        <dl className="mt-3 space-y-2 text-sm">
          {detail.answers.length === 0 && (
            <p className="text-[var(--color-muted)]">This type asks for no fields.</p>
          )}
          {detail.answers.map((answer) => (
            <div key={answer.code} className="flex flex-wrap items-baseline gap-2">
              <dt className="min-w-40 text-[var(--color-muted)]">
                {answer.label}
                {answer.withdrawn && (
                  <span
                    className="ms-1.5 text-xs"
                    title="No longer part of the form, but it was submitted"
                  >
                    (withdrawn field)
                  </span>
                )}
              </dt>
              <dd className="font-medium text-[var(--color-heading)]">
                {displayValue(answer.value)}
              </dd>
            </div>
          ))}
        </dl>
      </Card>

      <Card className="p-4">
        <h3 className="text-sm font-semibold text-[var(--color-heading)]">History</h3>
        <p className="mt-2 text-sm text-[var(--color-muted)]">
          {detail.previousOfSameType === 0
            ? 'Their first request of this kind.'
            : `${detail.previousOfSameType} previous ${
                detail.previousOfSameType === 1 ? 'request' : 'requests'
              } of this kind.`}
        </p>
        {detail.producesLetter && (
          <p className="mt-2 flex items-center gap-2 text-sm text-[var(--color-body)]">
            <FileText className="size-4" aria-hidden="true" />
            Approving this issues a letter.
          </p>
        )}
      </Card>

      {detail.attachments.length > 0 && (
        <Card className="p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-heading)]">
            <Paperclip className="size-4" aria-hidden="true" />
            Attached
          </h3>
          <ul className="mt-2 space-y-1.5">
            {detail.attachments.map((file) => (
              <li key={file.fileId} className="flex items-center justify-between gap-3 text-sm">
                <span className="min-w-0 truncate text-[var(--color-heading)]">{file.filename}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => void downloadFile(`/v1/files/${file.fileId}`, file.filename)}
                >
                  Open
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  )
}
